import axios from 'axios';
import { API_BASE_URL, handleApiError } from './_apiBase';

// ── Historical data & device properties ──────────────────────────────────────

/** Returns all devices from DB (including heat pump devices) for the alert rule form. */
export const fetchAllDevicesForAlerts = async (): Promise<{ id: string; name: string; type: string; status: string }[]> => {
    try {
        const res = await axios.get(`${API_BASE_URL}/alert-rules/all-devices`);
        return res.data;
    } catch (error) { return handleApiError(error, 'Hämta enheter för larm'); }
};

/** Returns rows for specific devices within a from/to time range (for historical chart). */
export const fetchHistoricalData = async (ieees: string[], from: string, to: string, properties?: string[]): Promise<any[]> => {
    try {
        const params = new URLSearchParams({ ieees: ieees.join(','), from, to });
        if (properties && properties.length > 0) params.set('properties', properties.join(','));
        const res = await axios.get(`${API_BASE_URL}/data/history?${params}`, { timeout: 30000 });
        return res.data;
    } catch (error) { return handleApiError(error, 'Hämta historik'); }
};

/** En rad ur data/recent och data/history. */
export interface DataRow {
    ieee_address: string;
    device_name?: string;
    property: string;
    value_num: number | null;
    value_str: string | null;
    timestamp: string;
    [key: string]: unknown;
}

/**
 * Senaste värdena för valda enheter och mätvärden (bevakningsfliken).
 * Kastar axios-felet oförändrat så anroparen kan känna igen en timeout (ECONNABORTED).
 */
export const fetchRecentReadingsRaw = async <T = DataRow>(ieees: string[], properties: string[], timeoutMs = 15000): Promise<T[]> => {
    const res = await axios.get<T[]>(
        `${API_BASE_URL}/data/recent?ieees=${encodeURIComponent(ieees.join(','))}&properties=${encodeURIComponent(properties.join(','))}`,
        { timeout: timeoutMs }
    );
    return res.data;
};

/**
 * Historik för en tidsperiod, med färdigformaterade tider (lokal tid utan tidszon, som servern vill ha).
 * Kastar axios-felet oförändrat så anroparen kan känna igen en timeout (ECONNABORTED).
 */
export const fetchHistoryRaw = async <T = DataRow>(
    q: { ieees: string; from: string; to: string; properties?: string },
    timeoutMs = 30000,
): Promise<T[]> => {
    const params = new URLSearchParams({ ieees: q.ieees, from: q.from, to: q.to });
    if (q.properties) params.set('properties', q.properties);
    const res = await axios.get<T[]>(`${API_BASE_URL}/data/history?${params}`, { timeout: timeoutMs });
    return res.data;
};

export interface DeviceProperty {
    key: string;
    label?: string;
    unit?: string;
    enumValues?: { value: string; text: string }[];
    lastValueNum?: number | null;
    lastValueStr?: string | null;
}

/** Returns the property keys (with optional labels) observed in the most recent data_value records for a device. */
export const fetchDeviceProperties = async (deviceIeee: string): Promise<DeviceProperty[] | null> => {
    try {
        const res = await axios.get(`${API_BASE_URL}/data/${encodeURIComponent(deviceIeee)}/properties`);
        return res.data;
    } catch (error: any) {
        console.error(`fetchDeviceProperties failed for ${deviceIeee}:`, error?.response?.status, error?.message);
        return null; // null = fetch failed (distinct from [] = fetched but no data)
    }
};

/** Bulk variant: fetches properties for many devices in a single round trip. */
export const fetchDevicePropertiesBulk = async (
    ieees: string[]
): Promise<Record<string, DeviceProperty[]> | null> => {
    if (ieees.length === 0) return {};
    try {
        const res = await axios.get(`${API_BASE_URL}/data/properties`, {
            params: { ieees: ieees.join(',') },
        });
        return res.data;
    } catch (error: any) {
        console.error('fetchDevicePropertiesBulk failed:', error?.response?.status, error?.message);
        return null;
    }
};
